import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import solicitudesService from '../../services/SolicitudesService';
import particularesApiService from '../../services/ParticularesApiService';
import ParticularService from '../../services/ParticularService';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_POLL_MINUTES = 1;
const MAX_BADGE = 99;

const isProfesorRole = (roleValue) => {
  if (!roleValue) return false;
  return String(roleValue).toLowerCase().includes('profesor');
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString();
};

const getSolicitudAudienceMeta = (item) => {
  const isOwn = Boolean(item?.esPropio ?? item?.EsPropio ?? false);
  return isOwn
    ? { className: 'own', shortLabel: 'Propio' }
    : { className: 'shared', shortLabel: 'Compartible' };
};

const SolicitudesDropdown = () => {
  const { user } = useAuth();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pollMinutes = useMemo(() => {
    const raw = process.env.REACT_APP_SOLICITUDES_POLL_MINUTES;
    const parsed = raw ? Number(raw) : DEFAULT_POLL_MINUTES;
    if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_POLL_MINUTES;
    return parsed;
  }, []);

  const shouldShow = useMemo(() => {
    return isProfesorRole(user?.role || user?.roleName || user?.rol);
  }, [user]);
  const isParticularContext = useMemo(() => {
    const roleValue = user?.role || user?.roleName || user?.rol || '';
    return Boolean(ParticularService.getActiveParticular()) ||
      String(roleValue).toLowerCase().includes('particular');
  }, [user]);

  const loadSolicitudes = useCallback(async () => {
    if (!shouldShow) return;
    setLoading(true);
    setError('');
    try {
      const response = isParticularContext
        ? await particularesApiService.getMisSolicitudesGptClasicos({ skip: 0, take: 20 })
        : await solicitudesService.getMisSolicitudesGptClasicos({ skip: 0, take: 20 });
      const list = response?.items || response?.Items || [];
      const unread = response?.totalUnread ?? response?.TotalUnread ?? 0;
      setItems(Array.isArray(list) ? list : []);
      setTotalUnread(Number.isFinite(unread) ? unread : 0);
    } catch (err) {
      setError(err?.message || 'No pudimos cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [isParticularContext, shouldShow]);

  useEffect(() => {
    loadSolicitudes();
    if (!shouldShow) return () => {};

    const intervalMs = pollMinutes * 60 * 1000;
    const intervalId = setInterval(loadSolicitudes, intervalMs);
    return () => clearInterval(intervalId);
  }, [loadSolicitudes, pollMinutes, shouldShow]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (!shouldShow) return null;

  const badgeValue = totalUnread > MAX_BADGE ? `${MAX_BADGE}+` : totalUnread;

  return (
    <div className="solicitudes-dropdown" ref={containerRef}>
      <button
        type="button"
        className="solicitudes-trigger"
        onClick={() => setOpen((prev) => !prev)}
        title="Solicitudes"
      >
        <i className="fas fa-bell"></i>
        {totalUnread > 0 && <span className="solicitudes-badge">{badgeValue}</span>}
      </button>

      {open && (
        <div className="solicitudes-panel">
          <div className="solicitudes-panel-header">
            <h4>Mis solicitudes</h4>
            <span>{loading ? 'Actualizando...' : `Sin leer: ${totalUnread}`}</span>
          </div>
          <div className="solicitudes-panel-body">
            {error && <div className="solicitudes-empty">{error}</div>}
            {!error && items.length === 0 && (
              <div className="solicitudes-empty">No tienes solicitudes recientes.</div>
            )}
            {!error && items.length > 0 && (
              <ul className="solicitudes-list">
                {items.map((item) => {
                  const audienceMeta = getSolicitudAudienceMeta(item);
                  return (
                    <li key={item.id || item.Id} className="solicitudes-item">
                      <div className="solicitudes-item-main">
                        <div className="solicitudes-item-top">
                          <span className="solicitudes-type">{item.tipoSolicitud || item.TipoSolicitud}</span>
                          <span className={`solicitudes-visibility ${audienceMeta.className}`}>
                            {audienceMeta.shortLabel}
                          </span>
                        </div>
                        <span className="solicitudes-date">{formatDate(item.fechaCreacion || item.FechaCreacion)}</span>
                      </div>
                      <span className={`solicitudes-status ${item.estado === 'Finalizada' || item.Estado === 'Finalizada' ? 'done' : 'pending'}`}>
                        {item.estado || item.Estado || 'En proceso'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SolicitudesDropdown;
